import { sha256Hex } from '@myplayer/gs1';
import {
  AUDIT_CHAIN_GENESIS_PREV_HASH,
  computeCanonicalAuditEntryHash,
} from '@myplayer/validation';
import type { AuditEntryRecord } from './audit-log-helpers.js';

export { AUDIT_CHAIN_GENESIS_PREV_HASH };

export interface AuditChainClientVerification {
  readonly valid: boolean;
  readonly status: 'VERIFIED' | 'TAMPERED' | 'UNCHAINED';
  readonly checkedCount: number;
  readonly unverifiedCount?: number | undefined;
  readonly brokenAtAuditId?: string;
  readonly brokenAtSequence?: number;
  readonly reason?: string;
}

/**
 * Pure TypeScript FIPS 180-4 SHA-256 implementation for browser and jsdom environments,
 * shared from `@myplayer/gs1` `sha256Hex` without relying on `node:crypto`.
 */
export const sha256HexBrowser = sha256Hex;

/**
 * Computes the canonical SHA-256 `entryHash` for an audit log entry, matching
 * `computeCanonicalAuditEntryHash` in `@myplayer/validation`, `functions/src/audit-chain.ts`,
 * and `services/partner-api/src/auth-rate-limit.ts`.
 */
export function computeAuditEntryHashClient(params: {
  readonly entry: AuditEntryRecord;
  readonly sequence: number;
  readonly prevHash: string;
}): string {
  const { entry, sequence, prevHash } = params;
  return computeCanonicalAuditEntryHash({
    entry,
    sequence,
    prevHash,
  });
}

/**
 * Attaches valid monotonic `sequence`, `prevHash`, and `entryHash` fields to a list of
 * audit entries (ordered chronologically by timestamp or input order).
 */
export function buildChainedAuditEntries(
  entries: readonly AuditEntryRecord[],
): readonly AuditEntryRecord[] {
  const chronological = [...entries].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  const chained: AuditEntryRecord[] = [];
  let prevHash = AUDIT_CHAIN_GENESIS_PREV_HASH;

  for (let i = 0; i < chronological.length; i++) {
    const item = chronological[i];
    if (!item) continue;
    const sequence = i + 1;
    const entryHash = computeAuditEntryHashClient({
      entry: item,
      sequence,
      prevHash,
    });
    chained.push({
      ...item,
      sequence,
      prevHash,
      entryHash,
    });
    prevHash = entryHash;
  }

  return chained;
}

/**
 * Returns audit entries loaded from Firestore unchanged so stored SHA-256 chain
 * fields are verified as-is and legacy unchained entries are shown as "unverified"
 * rather than synthesized in the browser.
 */
export function ensureCompleteAuditChain(
  entries: readonly AuditEntryRecord[],
): readonly AuditEntryRecord[] {
  return entries;
}

/**
 * Verifies the SHA-256 hash chain across loaded audit entries in the browser per `docs/AUDIT-LOGS.md`:
 * - Checks sequence numbers for missing/deleted entries (`entry[i].sequence === entry[i-1].sequence + 1`)
 * - Checks `entry[0].prevHash === 'GENESIS'` when starting from sequence 1
 * - Checks `entry[i].prevHash === entry[i-1].entryHash`
 * - Recomputes each entry's SHA-256 `entryHash` over its canonical fields to detect payload tampering
 * - Excludes legacy unchained entries from `checkedCount` and reports them in `unverifiedCount`
 */
export function verifyAuditChainClient(
  entries: readonly AuditEntryRecord[],
): AuditChainClientVerification {
  if (entries.length === 0) {
    return {
      valid: true,
      status: 'VERIFIED',
      checkedCount: 0,
      unverifiedCount: 0,
    };
  }

  const chainedEntries = entries.filter(
    (entry) =>
      entry.sequence !== undefined || entry.prevHash !== undefined || entry.entryHash !== undefined,
  );
  const unverifiedCount = entries.length - chainedEntries.length;

  if (chainedEntries.length === 0) {
    return {
      valid: true,
      status: 'UNCHAINED',
      checkedCount: 0,
      unverifiedCount,
    };
  }

  for (let i = 0; i < chainedEntries.length; i++) {
    const entry = chainedEntries[i];
    if (!entry) continue;
    if (
      typeof entry.sequence !== 'number' ||
      !Number.isInteger(entry.sequence) ||
      entry.sequence < 1
    ) {
      return {
        valid: false,
        status: 'TAMPERED',
        checkedCount: i,
        brokenAtAuditId: entry.auditId,
        reason: `Audit entry "${entry.auditId}" is missing a valid sequence number.`,
      };
    }
    if (typeof entry.prevHash !== 'string' || entry.prevHash.length === 0) {
      return {
        valid: false,
        status: 'TAMPERED',
        checkedCount: i,
        brokenAtAuditId: entry.auditId,
        brokenAtSequence: entry.sequence,
        reason: `Audit entry "${entry.auditId}" (#${String(entry.sequence)}) is missing prevHash.`,
      };
    }
    if (typeof entry.entryHash !== 'string' || entry.entryHash.length === 0) {
      return {
        valid: false,
        status: 'TAMPERED',
        checkedCount: i,
        brokenAtAuditId: entry.auditId,
        brokenAtSequence: entry.sequence,
        reason: `Audit entry "${entry.auditId}" (#${String(entry.sequence)}) is missing entryHash.`,
      };
    }
  }

  const sorted = [...chainedEntries].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

  for (let i = 0; i < sorted.length; i++) {
    const item = sorted[i];
    if (
      !item ||
      item.sequence === undefined ||
      item.prevHash === undefined ||
      item.entryHash === undefined
    ) {
      continue;
    }

    if (i === 0) {
      if (item.sequence === 1 && item.prevHash !== AUDIT_CHAIN_GENESIS_PREV_HASH) {
        return {
          valid: false,
          status: 'TAMPERED',
          checkedCount: 0,
          brokenAtAuditId: item.auditId,
          brokenAtSequence: item.sequence,
          reason: `Genesis entry "${item.auditId}" (#1) has invalid prevHash "${item.prevHash}" (expected "${AUDIT_CHAIN_GENESIS_PREV_HASH}").`,
        };
      }
    } else {
      const prevItem = sorted[i - 1];
      const expectedSeq = (prevItem?.sequence ?? 0) + 1;
      if (item.sequence !== expectedSeq) {
        return {
          valid: false,
          status: 'TAMPERED',
          checkedCount: i,
          brokenAtAuditId: item.auditId,
          brokenAtSequence: item.sequence,
          reason: `Sequence gap detected before "${item.auditId}": expected #${String(expectedSeq)}, found #${String(item.sequence)}.`,
        };
      }

      const expectedPrevHash = prevItem?.entryHash ?? '';
      if (item.prevHash !== expectedPrevHash) {
        return {
          valid: false,
          status: 'TAMPERED',
          checkedCount: i,
          brokenAtAuditId: item.auditId,
          brokenAtSequence: item.sequence,
          reason: `Broken hash link at "${item.auditId}" (#${String(item.sequence)}): prevHash does not match previous entryHash.`,
        };
      }
    }

    const recomputedHash = computeAuditEntryHashClient({
      entry: item,
      sequence: item.sequence,
      prevHash: item.prevHash,
    });
    if (recomputedHash !== item.entryHash) {
      return {
        valid: false,
        status: 'TAMPERED',
        checkedCount: i,
        brokenAtAuditId: item.auditId,
        brokenAtSequence: item.sequence,
        reason: `Entry hash mismatch at "${item.auditId}" (#${String(item.sequence)}): stored payload was modified after signing.`,
      };
    }
  }

  return {
    valid: true,
    status: 'VERIFIED',
    checkedCount: sorted.length,
    unverifiedCount,
  };
}

function escapeCsvCell(raw: string): string {
  const formulaSafe =
    raw.length > 0 && ['=', '+', '-', '@', '\t', '\r'].includes(raw.charAt(0)) ? `'${raw}` : raw;
  if (
    formulaSafe.includes(',') ||
    formulaSafe.includes('"') ||
    formulaSafe.includes('\n') ||
    formulaSafe.includes('\r')
  ) {
    return `"${formulaSafe.replace(/"/g, '""')}"`;
  }
  return formulaSafe;
}

export const AUDIT_CSV_HEADERS = [
  'sequence',
  'auditId',
  'timestamp',
  'orgId',
  'workspaceId',
  'actorUid',
  'actorType',
  'actorRole',
  'action',
  'resourceType',
  'resourceId',
  'version',
  'reason',
  'beforeSnapshot',
  'afterSnapshot',
  'correlationId',
  'prevHash',
  'entryHash',
] as const;

/**
 * Serialises audit log entries to CSV including `sequence`, `prevHash`, and `entryHash`
 * columns per `docs/AUDIT-LOGS.md` so compliance officers can verify chain continuity offline.
 */
export function serializeAuditEntriesToCsv(entries: readonly AuditEntryRecord[]): string {
  const lines: string[] = [AUDIT_CSV_HEADERS.join(',')];

  const ordered = [...entries].sort((a, b) => {
    if (a.sequence !== undefined && b.sequence !== undefined) {
      return a.sequence - b.sequence;
    }
    return b.timestamp.localeCompare(a.timestamp);
  });

  for (const entry of ordered) {
    const row = [
      entry.sequence !== undefined ? String(entry.sequence) : '',
      entry.auditId,
      entry.timestamp,
      entry.orgId,
      entry.workspaceId ?? '',
      entry.actorUid,
      entry.actorType,
      entry.actorRole ?? '',
      entry.action,
      entry.resourceType,
      entry.resourceId,
      entry.version !== undefined ? String(entry.version) : '',
      entry.reason ?? '',
      entry.beforeSnapshot !== null ? JSON.stringify(entry.beforeSnapshot) : '',
      entry.afterSnapshot !== null ? JSON.stringify(entry.afterSnapshot) : '',
      entry.correlationId,
      entry.prevHash ?? '',
      entry.entryHash ?? '',
    ].map(escapeCsvCell);

    lines.push(row.join(','));
  }

  return lines.join('\n');
}

export function subscribeOrganisationAuditLogs(params: {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly onEntries: (entries: readonly AuditEntryRecord[]) => void;
  readonly onError: (err: unknown) => void;
  readonly onSettled: () => void;
}): () => void {
  let unsubscribe: (() => void) | undefined;
  let active = true;
  void (async () => {
    try {
      const { collection, onSnapshot } = await import('firebase/firestore');
      if (!active) return;
      const { getFirebaseClients } = await import('./firebase.js');
      const { coerceAuditLogRecord } = await import('./audit-log-helpers.js');
      const { db } = getFirebaseClients();
      unsubscribe = onSnapshot(
        collection(db, 'organisations', params.orgId, 'auditLogs'),
        (snap) => {
          const coerced = snap.docs.map((d) =>
            coerceAuditLogRecord(
              d.id,
              d.data() as Readonly<Record<string, unknown>>,
              params.orgId,
              params.workspaceId,
            ),
          );
          params.onEntries(ensureCompleteAuditChain(coerced));
          params.onSettled();
        },
        (err) => {
          params.onError(err);
          params.onSettled();
        },
      );
    } catch {
      params.onSettled();
    }
  })();
  return () => {
    active = false;
    if (unsubscribe) unsubscribe();
  };
}
